import React, { useState, useEffect } from 'react'
import ReactCountryFlag from "react-country-flag"
import { useAuth } from "./api/authStore"
import useMobile from "./hooks/useMobile"
import moment from 'moment'
import LoadingSpinner from './components/LoadingSpinner'

// Компонент тултипа (мобильно-дружественный)
const Tooltip = ({ children, title, onLongPress, isCheckingSpeed }) => {
    const [show, setShow] = useState(false)
    const [isMobile, setIsMobile] = useState(false)
    const [touchTimer, setTouchTimer] = useState(null)
    
    // Определяем, мобильное ли устройство
    React.useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768 || 'ontouchstart' in window)
        }
        checkMobile()
        window.addEventListener('resize', checkMobile)
        return () => window.removeEventListener('resize', checkMobile)
    }, [])
    
    const handleTouchStart = (e) => {
        if (isMobile) {
            // Короткое касание - показываем тултип
            setShow(true)
            setTimeout(() => setShow(false), 3000)
            
            // Длительное зажатие - запускаем проверку скорости
            const timer = setTimeout(() => {
                if (onLongPress) {
                    console.log('Long press triggered in ConfirmDialog!') // Отладка
                    onLongPress(e)
                }
            }, 600) // 600ms для длительного нажатия
            
            setTouchTimer(timer)
        }
    }
    
    const handleTouchEnd = (e) => {
        if (isMobile && touchTimer) {
            // Если отпустили до истечения 600ms, отменяем длительное нажатие
            clearTimeout(touchTimer)
            setTouchTimer(null)
        }
    }
    
    const handleTouchCancel = (e) => {
        if (isMobile && touchTimer) {
            clearTimeout(touchTimer)
            setTouchTimer(null)
        }
    }
    
    return (
        <div 
            style={{ 
                position: 'relative', 
                display: 'inline-block',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none',
                WebkitTouchCallout: 'none',
                WebkitTapHighlightColor: 'transparent'
            }}
            onMouseEnter={() => !isMobile && setShow(true)}
            onMouseLeave={() => !isMobile && setShow(false)}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchCancel}
        >
            {/* Показываем спиннер только если идет проверка скорости */}
            {isCheckingSpeed ? (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <LoadingSpinner size={22} />
                </div>
            ) : (
                children
            )}
            {show && (
                <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'var(--medium-gray)',
                    color: 'var(--text-light)',
                    padding: '8px 10px',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 'var(--text-xs)',
                    whiteSpace: 'nowrap',
                    zIndex: 1000,
                    border: '1px solid var(--light-gray)',
                    boxShadow: 'var(--shadow-md)',
                    marginBottom: '5px',
                    // На мобильных устройствах делаем тултип более заметным
                    ...(isMobile && {
                        backgroundColor: 'var(--dark-gray)',
                        border: '2px solid var(--primary-orange)',
                        fontSize: 'var(--text-sm)',
                        padding: '10px 12px'
                    })
                }}>
                    {title}
                </div>
            )}
        </div>
    )
}


// Компонент для уведомлений
const CustomSnackbar = ({ open, onClose, message, severity = 'success' }) => {
    useEffect(() => {
        if (open) {
            // Автоматическое закрытие через 3 секунды
            const timer = setTimeout(() => {
                onClose()
            }, 3000)
            
            return () => clearTimeout(timer)
        }
    }, [open, onClose])

    if (!open) return null

    const getSeverityStyle = (severity) => {
        switch (severity) {
            case 'error':
                return {
                    background: '#dc3545',
                    color: 'var(--white)',
                    border: '1px solid #dc3545'
                }
            case 'success':
                return {
                    background: '#28a745',
                    color: 'var(--white)',
                    border: '1px solid #28a745'
                }
            default:
                return {
                    background: 'var(--primary-orange)',
                    color: 'var(--white)',
                    border: '1px solid var(--primary-orange)'
                }
        }
    }

    const style = getSeverityStyle(severity)

    return (
        <div style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10001,
            background: style.background,
            color: style.color,
            border: style.border,
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-md) var(--spacing-lg)',
            boxShadow: 'var(--shadow-card)',
            minWidth: '300px',
            textAlign: 'center'
        }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--spacing-md)'
            }}>
                <span style={{ fontSize: 'var(--text-sm)', fontWeight: '500' }}>
                    {message}
                </span>
                <button
                    onClick={onClose}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--white)',
                        fontSize: 'var(--text-lg)',
                        cursor: 'pointer',
                        padding: '0',
                        lineHeight: '1'
                    }}
                >
                    ✕
                </button>
            </div>
        </div>
    )
}

const formatFraudScore = (value) => {
    if (value === null || value === undefined || value === '') {
        return '-'
    }
    return value
}

const getProxyFraudScores = (proxy) => {
    const source = proxy?.scoreCheck ?? proxy?.score
    if (!source) {
        return null
    }
    const hasAnyScore = ['getipintel', 'ipqs', 'abuseipdb', 'scamalytics'].some(
        (key) => source[key] !== null && source[key] !== undefined
    )
    return hasAnyScore ? source : null
}

const ConfirmDialog = (props) => {
    const { getAccessTokenSilently } = useAuth()
    const { isMobile } = useMobile()

    const selectedProxy = props.selectedProxy

    const [openBuyError, setBuyError] = useState(false)
    const [openBuySuccess, setBuySuccess] = useState(false)
    const [alreadyPurchased, setAlreadyPutchased] = useState(false)
    const [isCheckingSpeed, setIsCheckingSpeed] = useState(false)
    const [proxySpeed, setProxySpeed] = useState(null)
    const [speedCheckTime, setSpeedCheckTime] = useState(null)
    const [fraudScores, setFraudScores] = useState(null)
    const [isCheckingFraud, setIsCheckingFraud] = useState(false)
    const [fraudCheckError, setFraudCheckError] = useState(false)

    // Инициализация скорости из данных прокси
    useEffect(() => {
        if (selectedProxy?.speed?.speed) {
            const speed = (selectedProxy.speed.speed / 1000 / 1000).toFixed(2)
            setProxySpeed(speed)
            setSpeedCheckTime(selectedProxy.speed.checkTime)
        } else {
            // Если нет данных о скорости, устанавливаем null
            setProxySpeed(null)
        }
        setFraudScores(getProxyFraudScores(selectedProxy))
    }, [selectedProxy])

    const handleClose = () => {
        props.setConfirmOpen(false)
    }

    const handleBuyPrivate = async() => {
        try {
            const proxy = {
                agentID: selectedProxy.id,
                exc: true,
                expPriceC: selectedProxy?.priceExcC,
                probe: false,
            }
            
            await props.apiClient.setToken(getAccessTokenSilently()).buyProxy(proxy)

            props.setConfirmOpen(false)
            setBuySuccess(true)
            props.setAfterBuy(true)
            props.setReloadActive(true)
        } catch (e) {
            if(e.response.data.error === 'paramInvalid'){

            } else if (e.response.data.error === 'agentAlreadyLeased'){
                setAlreadyPutchased(true)
            } else {
                setBuyError(true)
            }

            props.setConfirmOpen(false)
            
        }
    }

    const handleBuyShared = async() => {
        try {
            const proxy = {
                agentID: selectedProxy.id,
                exc: false,
                expPriceC: selectedProxy.priceShrC,
                probe: false,
            }

            await props.apiClient.setToken(getAccessTokenSilently()).buyProxy(proxy)

            props.setConfirmOpen(false)
            setBuySuccess(true)
            props.setAfterBuy(true)
            props.setReloadActive(true)
        } catch (e) {
            if(e.response && e.response.data && e.response.data.error === 'paramInvalid'){

            } else if (e.response && e.response.data && e.response.data.error === 'agentAlreadyLeased'){
                setAlreadyPutchased(true)
            } else {
                setBuyError(true)
            }

            props.setConfirmOpen(false)
        }
    }
    
    const handleBuyErrorClose = () => {
        setBuyError(false)
    }

    const handleBuySuccessClose = () => {
        setBuySuccess(false)
    }

    const handleAlreadyPurchasedClose = () => {
        setAlreadyPutchased(false)
    }

    // Функция для очистки agent ID от лишних путей
    const cleanAgentId = (agentId) => {
        if (!agentId) return null
        
        // Если agentId содержит полный путь, извлекаем только ID
        if (typeof agentId === 'string' && agentId.includes('/api/agents/')) {
            const match = agentId.match(/\/api\/agents\/([^/]+)/)
            return match ? match[1] : agentId
        }
        
        return agentId
    }

    const handleSpeedCheck = async () => {
        try {
            setIsCheckingSpeed(true)
            setProxySpeed(null)
            
            // Используем правильную логику получения agentId как в ProxyDetailsDialog
            const rawAgentId = selectedProxy?.agent || selectedProxy?.agentId || selectedProxy?.id
            const agentId = cleanAgentId(rawAgentId)
            
            // Отладочная информация
            console.log('ConfirmDialog speed check:', {
                selectedProxy: selectedProxy,
                rawAgentId: rawAgentId,
                cleanedAgentId: agentId
            })
            
            if (!agentId) {
                console.error('No valid agent ID found for speed check')
                setProxySpeed('Error')
                return
            }
            
            const response = await props.apiClient.setToken(getAccessTokenSilently()).checkProxySpeed(agentId)
            const speed = (response?.speed != null) ? (response.speed / 1000 / 1000).toFixed(2) : null
            
            setProxySpeed(speed)
            setSpeedCheckTime(response?.checkTime)
        } catch (e) {
            console.error('Speed check error:', e)
            setProxySpeed('Error')
        } finally {
            setIsCheckingSpeed(false)
        }
    }

    const handleFraudCheck = async () => {
        try {
            setIsCheckingFraud(true)
            setFraudCheckError(false)

            const rawAgentId = selectedProxy?.agent || selectedProxy?.agentId || selectedProxy?.id
            const agentId = cleanAgentId(rawAgentId)

            if (!agentId) {
                setFraudCheckError(true)
                return
            }

            const response = await props.apiClient
                .setToken(getAccessTokenSilently())
                .checkProxyFraudScore(agentId)

            setFraudScores(response)
        } catch (e) {
            console.error('Fraud score check error:', e)
            setFraudCheckError(true)
        } finally {
            setIsCheckingFraud(false)
        }
    }

    const handleFraudCheckErrorClose = () => {
        setFraudCheckError(false)
    }

    const maskedIp = selectedProxy?.ip?.replace(/_/g, '*') || 'N/A'

    const fraudScoreRows = [
        { label: 'Getipintel', key: 'getipintel' },
        { label: 'IPQS', key: 'ipqs' },
        { label: 'AbuseIPDB', key: 'abuseipdb' },
        { label: 'Scamalytics', key: 'scamalytics' },
    ]

    return (
        <>
            {/* Модальное окно подтверждения покупки */}
            {props.open && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10000
                }} onClick={handleClose}>
                    <div style={{
                        background: 'var(--medium-gray)',
                        border: '1px solid var(--light-gray)',
                        borderRadius: 'var(--radius-lg)',
                        padding: 'var(--spacing-2xl)',
                        maxWidth: '500px',
                        width: '90%',
                        boxShadow: 'var(--shadow-card)',
                        position: 'relative'
                    }} onClick={(e) => e.stopPropagation()}>
                        
                        {/* Кнопка закрытия */}
                        <button
                            onClick={handleClose}
                            style={{
                                position: 'absolute',
                                top: 'var(--spacing-md)',
                                right: 'var(--spacing-md)',
                                background: 'none',
                                border: 'none',
                                color: 'var(--text-muted)',
                                fontSize: 'var(--text-xl)',
                                cursor: 'pointer',
                                padding: '4px'
                            }}
                        >
                            ✕
                        </button>

                        {/* Заголовок */}
                        <h2 style={{
                            color: 'var(--text-light)',
                            fontSize: 'var(--text-2xl)',
                            fontWeight: '700',
                            marginBottom: 'var(--spacing-xl)',
                            textAlign: 'center',
                            background: 'var(--gradient-orange)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text'
                        }}>
                            Proxy Purchase Confirmation
                        </h2>

                        {/* Информация о прокси */}
                        <div style={{
                            background: 'var(--dark-gray)',
                            border: '1px solid var(--light-gray)',
                            borderRadius: 'var(--radius-md)',
                            padding: 'var(--spacing-lg)',
                            marginBottom: 'var(--spacing-lg)'
                        }}>
                            {/* Заголовок с флагом */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacing-md)',
                                marginBottom: 'var(--spacing-md)'
                            }}>
                                <div style={{
                                    width: '30px',
                                    height: '24px',
                                    borderRadius: 'var(--radius-sm)',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <ReactCountryFlag 
                                        countryCode={selectedProxy?.loc?.cc} 
                                        svg 
                                        style={{ width: '100%', height: '100%' }}
                                    />
                                </div>
                                <div>
                                    <h3 style={{
                                        color: 'var(--text-light)',
                                        fontSize: 'var(--text-lg)',
                                        fontWeight: '600',
                                        margin: '0 0 var(--spacing-xs) 0'
                                    }}>
                                        {selectedProxy?.loc?.cc} • Proxy Info
                                    </h3>
                                    <p style={{
                                        color: 'var(--text-muted)',
                                        fontSize: 'var(--text-sm)',
                                        margin: '0',
                                        lineHeight: '1.4'
                                    }}>
                                        {selectedProxy?.loc?.reg} • {selectedProxy?.loc?.city} • {selectedProxy?.loc?.zip} • {selectedProxy?.loc?.isp}
                                    </p>
                                </div>
                            </div>

                            {/* IP и fraud score */}
                            <div style={{
                                display: 'flex',
                                gap: 'var(--spacing-md)',
                                paddingTop: 'var(--spacing-sm)',
                                borderTop: '1px solid var(--light-gray)',
                                alignItems: isMobile ? 'stretch' : 'flex-start',
                                flexDirection: isMobile ? 'column' : 'row',
                            }}>
                                <div style={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '4px',
                                    width: isMobile ? '100%' : 'auto',
                                }}>
                                    <div style={{
                                        fontSize: 'var(--text-sm)',
                                        lineHeight: '1.6',
                                        color: 'var(--text-light)',
                                    }}>
                                        <span style={{ color: 'var(--text-muted)' }}>IP Address:</span>{' '}
                                        <span style={{
                                            fontFamily: 'var(--font-mono)',
                                            fontWeight: '600',
                                        }}>
                                            {maskedIp}
                                        </span>
                                    </div>
                                    {fraudScoreRows.map(({ label, key }) => (
                                        <div
                                            key={key}
                                            style={{
                                                fontSize: 'var(--text-sm)',
                                                lineHeight: '1.6',
                                                color: 'var(--text-light)',
                                            }}
                                        >
                                            <span style={{ color: 'var(--text-muted)' }}>{label}:</span>{' '}
                                            <span style={{
                                                fontFamily: 'var(--font-mono)',
                                                fontWeight: '600',
                                            }}>
                                                {formatFraudScore(fraudScores?.[key])}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    onClick={handleFraudCheck}
                                    disabled={isCheckingFraud}
                                    style={{
                                        minWidth: isMobile ? '100%' : '110px',
                                        height: '32px',
                                        flexShrink: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0 12px',
                                        background: 'var(--dark-gray)',
                                        border: '1px solid var(--light-gray)',
                                        borderRadius: 'var(--radius-sm)',
                                        color: 'var(--text-muted)',
                                        fontSize: 'var(--text-sm)',
                                        fontWeight: '500',
                                        whiteSpace: 'nowrap',
                                        opacity: isCheckingFraud ? 0.7 : 1,
                                        cursor: isCheckingFraud ? 'not-allowed' : 'pointer',
                                        transition: 'background var(--transition-fast), color var(--transition-fast), border-color var(--transition-fast)',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isCheckingFraud) {
                                            e.currentTarget.style.background = 'var(--medium-gray)'
                                            e.currentTarget.style.color = 'var(--text-light)'
                                            e.currentTarget.style.borderColor = 'var(--light-gray)'
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isCheckingFraud) {
                                            e.currentTarget.style.background = 'var(--dark-gray)'
                                            e.currentTarget.style.color = 'var(--text-muted)'
                                            e.currentTarget.style.borderColor = 'var(--light-gray)'
                                        }
                                    }}
                                >
                                    {isCheckingFraud ? (
                                        <LoadingSpinner size={22} />
                                    ) : (
                                        'check 0.05$'
                                    )}
                                </button>
                            </div>

                        </div>

                        {/* Информация о покупке */}
                        <div style={{ marginBottom: 'var(--spacing-xl)' }}>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 'var(--spacing-md)'
                            }}>
                                <p style={{
                                    color: 'var(--text-light)',
                                    fontSize: 'var(--text-base)',
                                    margin: '0',
                                    lineHeight: '1.5'
                                }}>
                                    The proxy will be rented by you for <strong style={{ color: 'var(--primary-orange)' }}>24 hours</strong>.
                                </p>
                                
                                <p style={{
                                    color: 'var(--text-light)',
                                    fontSize: 'var(--text-base)',
                                    margin: '0',
                                    lineHeight: '1.5'
                                }}>
                                    You can refund proxy if it doesn't work in first <strong style={{ color: 'var(--primary-orange)' }}>1 hour</strong>.
                                </p>
                                
                                <p style={{
                                    color: 'var(--text-muted)',
                                    fontSize: 'var(--text-sm)',
                                    margin: '0',
                                    lineHeight: '1.5'
                                }}>
                                    Confirming purchase you agree with our{' '}
                                    <a 
                                        href="https://xsocks.pro/terms-conditions.html"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            color: 'var(--primary-orange)',
                                            textDecoration: 'none'
                                        }}
                                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                    >
                                        terms and conditions
                                    </a>
                                </p>
                            </div>
                        </div>

                        {/* Кнопки */}
                        <div style={{
                            display: 'flex',
                            gap: 'var(--spacing-md)',
                            justifyContent: 'center',
                            flexDirection: isMobile ? 'column' : 'row'
                        }}>
                            {/* Кнопка проверки скорости - показываем только на десктопе */}
                            {!isMobile && (
                                <button
                                    onClick={handleSpeedCheck}
                                    className="btn btn-outline"
                                    style={{ 
                                        minWidth: '140px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                        opacity: isCheckingSpeed ? 0.7 : 1,
                                        cursor: isCheckingSpeed ? 'not-allowed' : 'pointer'
                                    }}
                                    disabled={isCheckingSpeed}
                                >
                                    {isCheckingSpeed ? (
                                        <LoadingSpinner size={22} />
                                    ) : (
                                        <Tooltip 
                                            title={speedCheckTime ? moment(speedCheckTime).fromNow() : 'Speed test result'}
                                        >
                                            <span>
                                                {proxySpeed != null ? `${proxySpeed} Mbps` : 'Check speed'}
                                            </span>
                                        </Tooltip>
                                    )}
                                </button>
                            )}
                            
                            {/* На мобильных устройствах показываем только результат скорости */}
                            {isMobile && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '8px 12px',
                                    background: 'var(--dark-gray)',
                                    border: '1px solid var(--light-gray)',
                                    borderRadius: 'var(--radius-sm)',
                                    color: 'var(--text-light)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '500',
                                    marginBottom: 'var(--spacing-sm)'
                                }}>
                                    <Tooltip 
                                        title={speedCheckTime ? moment(speedCheckTime).fromNow() : 'Hold to test speed'}
                                        onLongPress={() => {
                                          handleSpeedCheck()
                                        }}
                                        isCheckingSpeed={isCheckingSpeed}
                                    >
                                        <span>
                                            {proxySpeed != null ? `${proxySpeed} Mbps` : 'Check speed'}
                                        </span>
                                    </Tooltip>
                                </div>
                            )}
                            {selectedProxy?.priceExcC !== undefined ? (
                                <>
                                    <button
                                        onClick={handleBuyShared}
                                        className="btn btn-buy"
                                        style={{ 
                                            minWidth: isMobile ? '100%' : '140px', 
                                            height: '32px',
                                            marginBottom: isMobile ? 'var(--spacing-sm)' : '0'
                                        }}
                                    >
                                        Buy Shared ${selectedProxy?.priceShrC ? (selectedProxy.priceShrC/100).toFixed(2) : '0.00'} 
                                    </button>
                                    <button
                                        onClick={handleBuyPrivate}
                                        className="btn btn-buy"
                                        style={{ 
                                            minWidth: isMobile ? '100%' : '140px', 
                                            height: '32px'
                                        }}
                                    >
                                        Buy Private ${selectedProxy?.priceExcC ? (selectedProxy.priceExcC/100).toFixed(2) : '0.00'} 
                                    </button>
                                </>
                            ) : (
                                <button
                                    onClick={handleBuyShared}
                                    className="btn btn-buy"
                                    style={{ 
                                        minWidth: isMobile ? '100%' : '150px', 
                                        height: '32px'
                                    }}
                                >
                                    Buy Shared ${selectedProxy?.priceShrC ? (selectedProxy.priceShrC/100).toFixed(2) : '0.00'} 
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Уведомления */}
            <CustomSnackbar
              open={openBuyError}
              onClose={handleBuyErrorClose}
                message="Error purchasing proxy."
                    severity="error"
            />

            <CustomSnackbar
              open={alreadyPurchased}
              onClose={handleAlreadyPurchasedClose}
                message="Current proxy was already purchased."
                    severity="warning"
            />

            <CustomSnackbar
              open={openBuySuccess}
              onClose={handleBuySuccessClose}
                message="Proxy purchased successfully!"
              severity="success"
            />

            <CustomSnackbar
              open={fraudCheckError}
              onClose={handleFraudCheckErrorClose}
              message="Error checking fraud score."
              severity="error"
            />
        </>
    )
}

export default ConfirmDialog